import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Plus, X, TrendingUp, ChevronDown, Check, AlertTriangle, Pencil } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, Card, Spinner, Empty, chf } from "@/components/common";
import { useI18n } from "@/context/I18nContext";

const STATUS_TONE = {
  offen: "bg-white/10 text-gray-300 border-white/10",
  angebot_vorbereitet: "bg-blue-400/15 text-blue-300 border-blue-400/30",
  kunde_interessiert: "bg-[#A3E635]/15 text-[#A3E635] border-[#A3E635]/30",
  angenommen: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30",
  abgelehnt: "bg-white/5 text-gray-500 border-white/10",
};
const EMPTY = { client_id: "", title: "", benefit: "", description: "", priority: "mittel", estimated_value: "", recommended_module_id: "" };

export default function NextSteps() {
  const { t } = useI18n();
  const [steps, setSteps] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [recs, setRecs] = useState([]);
  const [clients, setClients] = useState([]);
  const [filter, setFilter] = useState("alle");
  const [showRecs, setShowRecs] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const openNew = () => { setEditId(null); setForm(EMPTY); setShowNew(true); };
  const startEdit = (s) => {
    setForm({ client_id: s.client_id || "", title: s.title || "", benefit: s.benefit || "", description: s.description || "", priority: s.priority || "mittel", estimated_value: s.estimated_value ?? "", recommended_module_id: s.recommended_module_id || "" });
    setEditId(s.id); setShowNew(true);
  };
  const closeModal = () => { setShowNew(false); setEditId(null); setForm(EMPTY); };

  const load = () => {
    api.get("/next-steps").then((r) => setSteps(r.data));
    api.get("/next-steps/recommendations").then((r) => setRecs(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get("/next-steps/config").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  }, []);

  const counts = useMemo(() => {
    const c = { offen: 0, angebot_vorbereitet: 0, kunde_interessiert: 0, angenommen: 0, potenzial: 0 };
    (steps || []).forEach((s) => {
      if (c[s.status] !== undefined) c[s.status]++;
      if (["offen", "angebot_vorbereitet", "kunde_interessiert"].includes(s.status)) c.potenzial += s.estimated_value || 0;
    });
    return c;
  }, [steps]);

  if (!steps || !cfg) return <Spinner />;
  const slabel = (k) => t(cfg.status_labels[k] || k);

  const changeStatus = async (s, status) => {
    await api.put(`/next-steps/${s.id}`, { status });
    toast.success(status === "angenommen" ? t("Angenommen — Modul aktiviert") : t("Status aktualisiert"));
    load();
  };

  const addFromRec = async (clientId, rec) => {
    await api.post("/next-steps", {
      client_id: clientId, title: rec.name, benefit: rec.reason || rec.description,
      recommended_module_id: rec.module_id, estimated_value: rec.on_request ? null : rec.price_chf,
    });
    toast.success(t("«{name}» als Upsell aufgenommen").replace("{name}", rec.name));
    load();
  };

  const errs = [];
  if (showNew) {
    if (!form.client_id) errs.push(t("Kunde fehlt."));
    if (!form.title.trim()) errs.push(t("Titel fehlt."));
  }
  const save = async () => {
    if (errs.length) { toast.error(t("Bitte Pflichtfelder prüfen.")); return; }
    setSaving(true);
    try {
      const ev = form.estimated_value !== "" && form.estimated_value != null ? Number(form.estimated_value) : null;
      if (editId) {
        await api.put(`/next-steps/${editId}`, { title: form.title, benefit: form.benefit, description: form.description, priority: form.priority, estimated_value: ev });
        toast.success(t("Empfehlung aktualisiert"));
      } else {
        await api.post("/next-steps", { ...form, estimated_value: ev, recommended_module_id: form.recommended_module_id || null });
        toast.success(t("Upsell-Empfehlung erstellt"));
      }
      closeModal(); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("Fehler beim Speichern.")); }
    finally { setSaving(false); }
  };

  const shown = steps.filter((s) => filter === "alle" || s.status === filter);
  const STATS = [["offen", "Offen"], ["angebot_vorbereitet", "Angebot vorbereitet"], ["kunde_interessiert", "Kunde interessiert"], ["angenommen", "Angenommen"]];

  return (
    <div className="fade-in">
      <PageHeader title={t("Nächste Schritte")} subtitle={t("Upsell-Pipeline: Empfehlungen, Kundeninteresse und Abschlüsse pro Kunde.")}>
        <button onClick={openNew} data-testid="new-upsell-btn"
          className="bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2.5 px-5 rounded-lg inline-flex items-center gap-2"><Plus className="w-4 h-4" /> {t("Empfehlung")}</button>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6" data-testid="upsell-stats">
        {STATS.map(([k, l]) => (
          <Card key={k} className="p-4" data-testid={`upsell-stat-${k}`}>
            <div className="text-2xl font-black tracking-tight">{counts[k]}</div>
            <div className="text-[11px] text-gray-400 mt-0.5">{t(l)}</div>
          </Card>
        ))}
        <Card className="p-4 border-[#A3E635]/30" data-testid="upsell-stat-potenzial">
          <div className="text-2xl font-black tracking-tight text-[#A3E635]">{chf(counts.potenzial)}</div>
          <div className="text-[11px] text-gray-400 mt-0.5">{t("Offenes Potenzial / Mt.")}</div>
        </Card>
      </div>

      {/* Empfehlungen aus fehlenden Modulen */}
      <Card className="p-0 mb-6 overflow-hidden">
        <button onClick={() => setShowRecs((v) => !v)} data-testid="toggle-recs" className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/[0.02]">
          <span className="font-bold flex items-center gap-2"><TrendingUp className="w-4 h-4 text-[#A3E635]" /> {t("Upsell-Empfehlungen ({n} Möglichkeiten)").replace("{n}", recs.reduce((a, r) => a + r.recommendations.length, 0))}</span>
          <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${showRecs ? "rotate-180" : ""}`} />
        </button>
        {showRecs && (
          <div className="px-5 pb-5 space-y-4">
            {recs.length === 0 ? <p className="text-sm text-gray-500">{t("Alle Kunden haben bereits alle Module — kein offenes Upsell-Potenzial.")}</p> : recs.map((r) => (
              <div key={r.client_id} data-testid={`rec-client-${r.client_id}`}>
                <div className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">{r.company_name}</div>
                <div className="flex gap-2 flex-wrap">
                  {r.recommendations.map((rec) => (
                    <button key={rec.module_id} onClick={() => addFromRec(r.client_id, rec)} data-testid={`rec-add-${r.client_id}-${rec.module_id}`} title={rec.reason || rec.description}
                      className="group inline-flex items-center gap-2 bg-white/5 hover:bg-[#A3E635]/15 border border-white/10 hover:border-[#A3E635]/40 rounded-lg px-3 py-1.5 text-xs transition-colors">
                      {rec.priority === "hoch" ? <span className="w-1.5 h-1.5 rounded-full bg-[#A3E635] shrink-0" title={t("Hohe Priorität")} /> : <Plus className="w-3.5 h-3.5 text-[#A3E635]" />}
                      <span className="font-semibold">{rec.name}</span>
                      <span className="text-gray-500">{rec.on_request ? t("auf Anfrage") : `${chf(rec.price_chf)}/Mt.`}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Pipeline-Filter */}
      <div className="flex gap-2 mb-5 flex-wrap">
        {["alle", ...cfg.status_order].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`upsell-filter-${s}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${filter === s ? "bg-[#A3E635] text-black" : "bg-white/5 text-gray-400 hover:text-white"}`}>
            {s === "alle" ? t("Alle") : slabel(s)}
          </button>
        ))}
      </div>

      {shown.length === 0 ? <Empty text={t("Keine Empfehlungen in dieser Ansicht.")} /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger">
          {shown.map((s) => (
            <Card key={s.id} className="p-5 flex flex-col" data-testid={`nextstep-${s.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="w-10 h-10 rounded-lg bg-[#A3E635]/15 border border-[#A3E635]/30 flex items-center justify-center shrink-0"><Sparkles className="w-5 h-5 text-[#A3E635]" /></div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button onClick={() => startEdit(s)} data-testid={`nextstep-edit-${s.id}`} title={t("Bearbeiten")} className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/10 transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                  <span className={`text-[11px] px-2.5 py-1 rounded-full border font-semibold ${STATUS_TONE[s.status]}`} data-testid={`nextstep-status-${s.id}`}>{slabel(s.status)}</span>
                </div>
              </div>
              <div className="mt-3 flex-1">
                <div className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/10 text-gray-300 inline-block mb-2">{s.company_name}</div>
                <h3 className="font-bold">{s.title}</h3>
                <p className="text-gray-400 text-sm mt-1">{s.benefit || s.description}</p>
                <div className="flex items-center gap-3 mt-2 text-xs">
                  {s.estimated_value != null && <span className="text-[#A3E635] font-bold">{chf(s.estimated_value)}/Mt.</span>}
                  {s.module && <span className="text-gray-500">{s.module.name}</span>}
                </div>
              </div>
              <select value={s.status} onChange={(e) => changeStatus(s, e.target.value)} data-testid={`nextstep-select-${s.id}`}
                className="mt-4 w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#A3E635]">
                {cfg.status_order.map((k) => <option key={k} value={k}>{slabel(k)}</option>)}
              </select>
            </Card>
          ))}
        </div>
      )}

      {showNew && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto bg-black/70 backdrop-blur-sm" data-testid="new-upsell-modal"
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="w-full max-w-lg bg-[#121217] border border-white/10 rounded-2xl p-6 my-8">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#A3E635]" /> {editId ? t("Empfehlung bearbeiten") : t("Neue Empfehlung")}</h3>
              <button onClick={closeModal} className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/5"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <Field t={t("Kunde *")}><select value={form.client_id} disabled={!!editId} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="upsell-client" className={`${cls} ${editId ? "opacity-60 cursor-not-allowed" : ""}`}><option value="">{t("— wählen —")}</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}</select></Field>
              <Field t={t("Titel *")}><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="upsell-title" className={cls} placeholder={t("z. B. SEO-Booster")} /></Field>
              <Field t={t("Nutzen für den Kunden")}><textarea value={form.benefit} onChange={(e) => setForm({ ...form, benefit: e.target.value })} data-testid="upsell-benefit" rows={2} className={cls} placeholder={t("Was bringt es dem Kunden?")} /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field t={t("Priorität")}><select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className={cls}><option value="niedrig">{t("Niedrig")}</option><option value="mittel">{t("Mittel")}</option><option value="hoch">{t("Hoch")}</option></select></Field>
                <Field t={t("Wert CHF / Mt.")}><input type="number" value={form.estimated_value} onChange={(e) => setForm({ ...form, estimated_value: e.target.value })} data-testid="upsell-value" className={cls} placeholder={t("z. B. 49")} /></Field>
              </div>
            </div>
            {errs.length > 0 && <div className="mt-4 space-y-1.5" data-testid="upsell-warnings">{errs.map((e, i) => <div key={i} className="text-sm text-red-300 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> {e}</div>)}</div>}
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={closeModal} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5">{t("Abbrechen")}</button>
              <button onClick={save} disabled={saving || errs.length > 0} data-testid="upsell-save" className="bg-[#A3E635] hover:bg-[#BEF264] disabled:opacity-40 text-black font-bold py-2 px-5 rounded-lg inline-flex items-center gap-2"><Check className="w-4 h-4" /> {editId ? t("Speichern") : t("Erstellen")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const cls = "w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#A3E635]";
const Field = ({ t, children }) => (
  <div><label className="text-[11px] font-bold uppercase tracking-wider text-gray-400">{t}</label><div className="mt-1">{children}</div></div>
);
